/* eslint-disable max-len */
/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Col } from 'reactstrap';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';
import { setCurrencyFormat, colorsCombination } from '../../../../utils/helpers';

export const CustomTooltip = (props) => {
    // eslint-disable-next-line react/prop-types
    const { active, payload, category, categorisedBasedOn } = props;
    // we don't need to check payload[0] as there's a better prop for this purpose
    if (!active) {
        // I think returning null works based on this: http://recharts.org/en-US/examples/CustomContentOfTooltip
        return null
    }
    // mutating props directly is against react's conventions
    // so we create a new payload with the name and value fields set to what we want
    // eslint-disable-next-line react/prop-types
    const data = payload[0]?.payload;
    const newPayload = [
        {
            name: 'Date',
            // all your data which created the tooltip is located in the .payload property
            // eslint-disable-next-line react/prop-types
            value: data?.currentMonthDataKey,
            // you can also add "unit" here if you need it
        },
        {
            name: 'Amount',
            value: setCurrencyFormat(data.Amount, true)
        },
        {
            name: 'Quantity',
            value: setCurrencyFormat(data.Quantity, false)
        },
        {
            name: 'Asin - Title',
            value: category
        },
        // ...payload,
    ];

    // we render the default, but with our overridden payload
    return <DefaultTooltipContent {...props} payload={newPayload} />;
};

const VirtualBundleSalesLineChart = ({
    dir, showTitle, categorisedBasedOn, index,
    currentYearMonthDayWise, height, category }) => {
    const { t } = useTranslation('common');
    const [color1] = colorsCombination[index % 10];
    const [color2] = colorsCombination[index % 5];

    const dataToShow = [];

    Object.keys((currentYearMonthDayWise)).forEach((dayMonthYear) => {
        const [day, month, year, dayofyear] = dayMonthYear.split('-');
        const { currentMonthAmount, soldQuantity } = currentYearMonthDayWise?.[dayMonthYear]
            ?.reduce((acc, item) => {
                acc.currentMonthAmount = Number(Number(Number(acc.currentMonthAmount) + Number(item.amount)).toFixed(2));
                acc.soldQuantity = Number(Number(Number(acc.soldQuantity) + Number(item.soldQuantity)).toFixed(2));
                return acc
            }, { currentMonthAmount: 0, soldQuantity: 0 }) ?? { currentMonthAmount: 0, soldQuantity: 0 };

        const currentMonthDataKey = `${day}, ${month} ${year}`;

        dataToShow.push({
            "Amount": currentMonthAmount,
            "day": `${currentMonthDataKey}`,
            currentMonthDataKey,
            category,
            "Quantity": soldQuantity
        })
    })

    return (
        <Col xs={12}>
            <Card>
                <CardBody>
                    {showTitle ? <div className="card__title">
                        <h5 className="bold-text">{t('comparemonthlysales.compareDays')}</h5>
                    </div> : null}
                    <div dir="ltr">
                        <ResponsiveContainer height={height}>
                            <LineChart
                                data={dataToShow}
                                margin={{
                                    top: 0, right: 0, left: 25, bottom: 0,
                                }}
                            >
                                <XAxis dataKey="day" reversed={dir === 'rtl'} />
                                <YAxis orientation={dir === 'rtl' ? 'right' : 'left'}
                                    tickFormatter={item => setCurrencyFormat(item, true)}
                                    width={90}
                                    allowDataOverflow
                                />
                                <CartesianGrid vertical={false} horizontal  stroke="#FF0000" strokeWidth={2} />
                                <Tooltip content={<CustomTooltip category={category} categorisedBasedOn={categorisedBasedOn} />} />
                                <Legend />
                                <Line dot={false} type="monotone" dataKey="Amount" stroke='#3366ff' strokeWidth={2} />
                                <Line dot={false} type="monotone" dataKey="Quantity" stroke='#F580D2' strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </CardBody>
            </Card>
        </Col>
    );
};

VirtualBundleSalesLineChart.propTypes = {
    dir: PropTypes.string.isRequired,
    currentYearMonthDayWise: PropTypes.object.isRequired,
    height: PropTypes.number.isRequired,
    showTitle: PropTypes.bool,
    category: PropTypes.string.isRequired,
    categorisedBasedOn: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired
};

VirtualBundleSalesLineChart.defaultProps = {
    showTitle: true
}

export default VirtualBundleSalesLineChart;
